import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Network,
  ListOrdered,
  Gavel,
  UserSquare,
  Landmark,
  BookOpen,
  FileText,
  Link2,
  ScrollText,
} from "lucide-react";
import NavbarEnlaces from "../../../layout/NavbarEnlaces";
import Modal from "../../../layout/Modal/Modal";
import ModalIMG from "../../../layout/Modal/ModalIMG";
import ListBadge from "../../../layout/ListBadge";
import {
  dataEstructuraOrganica,
  dataNormatividad,
  dataPOA,
  dataSemblanza,
  dataDeclaracionPatrimonial,
  dataCodigoEtica,
  dataInformes,
  dataConvenios,
  dataAcuerdosOIC,
  dataResoluciones,
} from "../../../data/dataOIC";
import "../EnlacesStyle.css";
import "./Oicc.css";

// Ícono de Programas: ventana con terminal y símbolo ">_" en blanco brillante de alto contraste
const ProgramasIcon = ({ size = 62, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect
      width="18"
      height="18"
      x="3"
      y="3"
      rx="2"
      ry="2"
      fill="url(#enlacesGradient)"
      stroke="url(#enlacesGradientCanvas)"
      strokeWidth={1.5}
    />
    <path d="m7 11 2-2-2-2" stroke="#ffffff" strokeWidth={2} fill="none" />
    <path d="M11 13h4" stroke="#ffffff" strokeWidth={2} fill="none" />
  </svg>
);

// Ícono de Galería: dos cuadros superpuestos con paisaje montañoso y sol en blanco brillante
const GaleriaIcon = ({ size = 62, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect
      x="7"
      y="2"
      width="15"
      height="15"
      rx="2"
      fill="url(#enlacesGradient)"
      stroke="url(#enlacesGradientCanvas)"
      strokeWidth={1.5}
    />
    <rect
      x="2"
      y="7"
      width="15"
      height="15"
      rx="2"
      fill="url(#enlacesGradient)"
      stroke="url(#enlacesGradientCanvas)"
      strokeWidth={1.5}
    />
    <circle cx="7" cy="12" r="1.5" fill="#ffffff" stroke="#ffffff" strokeWidth={0.5} />
    <path
      d="m17 19-4.5-4.5a1.5 1.5 0 0 0-2.12 0L2 21.5"
      stroke="#ffffff"
      strokeWidth={2}
      fill="none"
    />
  </svg>
);

// Ícono de Acuerdos OIC: documento con esquina doblada en blanco sólido y palomita de verificación
const AcuerdosOicIcon = ({ size = 62, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* 1. Cuerpo principal del documento con relleno degradado */}
    <path
      d="M10.5 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v6"
      fill="url(#enlacesGradient)"
      stroke="url(#enlacesGradientCanvas)"
      strokeWidth={1.5}
    />
    {/* 2. Doblez de la esquina superior derecha en BLANCO SÓLIDO (#ffffff), colocado después del cuerpo */}
    <polygon
      points="14,2 14,8 20,8"
      fill="#ffffff"
      stroke="url(#enlacesGradientCanvas)"
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
    {/* 3. Palomita / checkmark de verificación en blanco brillante */}
    <path
      d="m14 20 2 2 4-4"
      fill="none"
      stroke="#ffffff"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Oic = () => {
  useEffect(() => {
    document.title = `OIC`;
  }, []);

  return (
    <>
      <svg
        width="0"
        height="0"
        style={{ position: "absolute", pointerEvents: "none" }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="enlacesGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#A90786" />
            <stop offset="100%" stopColor="#5A2C6E" />
          </linearGradient>
          <linearGradient id="enlacesGradientCanvas" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="24" y2="24">
            <stop offset="0%" stopColor="#A90786" />
            <stop offset="100%" stopColor="#5A2C6E" />
          </linearGradient>
        </defs>
      </svg>
      <div className="margin-bottom-2">
        <NavbarEnlaces title="Órgano Interno de Control" />
        <div className="Content__grid">
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#EstructuraOrganica"
            >
              <Network
                size={62}
                fill="url(#enlacesGradient)"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={1.5}
                className="OIC__icon OIC__icon--network"
              />
            </a>
            <p className="mt-2">Estructura Orgánica</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#Atribuciones"
            >
              <ListOrdered
                size={62}
                fill="url(#enlacesGradient)"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={1.8}
                className="OIC__icon OIC__icon--list"
              />
            </a>
            <p className="mt-2">Atribuciones</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#Normatividad"
            >
              <Gavel
                size={62}
                fill="url(#enlacesGradient)"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={1.5}
                className="OIC__icon OIC__icon--gavel"
              />
            </a>
            <p className="mt-2">Normatividad</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#Programas"
            >
              <ProgramasIcon
                size={62}
                className="OIC__icon OIC__icon--terminal"
              />
            </a>
            <p className="mt-2">Programas</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#Semblanza"
            >
              <UserSquare
                size={62}
                fill="url(#enlacesGradient)"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={1.5}
                className="OIC__icon OIC__icon--user"
              />
            </a>
            <p className="mt-2">Semblanza</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#DeclaracionPatrimonial"
            >
              <Landmark
                size={62}
                fill="url(#enlacesGradient)"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={1.5}
                className="OIC__icon OIC__icon--landmark"
              />
            </a>
            <p className="mt-2">Declaración Patrimonial</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#CodigodeEtica"
            >
              <BookOpen
                size={62}
                fill="url(#enlacesGradient)"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={1.5}
                className="OIC__icon OIC__icon--book"
              />
            </a>
            <p className="mt-2">Código de Ética</p>
          </div>
          <div>
            <Link to="/GaleriaOIC" className="OIC__link">
              <GaleriaIcon
                size={62}
                className="OIC__icon OIC__icon--images"
              />
            </Link>
            <p className="mt-2">Galería</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#Informes"
            >
              <FileText
                size={62}
                fill="url(#enlacesGradient)"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={1.5}
                className="OIC__icon OIC__icon--informes"
              />
            </a>
            <p className="mt-2">Informes</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#Convenios"
            >
              <Link2
                size={62}
                fill="none"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={2.4}
                className="OIC__icon OIC__icon--convenios"
              />
            </a>
            <p className="mt-2">Convenios</p>
          </div>
          <div>
            <Link to="/AcuerdosOIC" className="OIC__link">
              <AcuerdosOicIcon
                size={62}
                className="OIC__icon OIC__icon--acuerdos"
              />
            </Link>
            <p className="mt-2">Acuerdos OIC</p>
          </div>
          <div>
            <a
              href="#/"
              className="OIC__link"
              rel="noreferrer"
              data-bs-toggle="modal"
              data-bs-target="#Resoluciones"
            >
              <ScrollText
                size={62}
                fill="url(#enlacesGradient)"
                stroke="url(#enlacesGradientCanvas)"
                strokeWidth={1.5}
                className="OIC__icon OIC__icon--resoluciones"
              />
            </a>
            <p className="mt-2">Resoluciones</p>
          </div>
        </div>
        <Modal
          idModal="EstructuraOrganica"
          titleModal="Estructura Orgánica del Órgano Interno de Control"
          sizeModal=""
          contentModal={
            <ListBadge
              ifNumbered={""}
              listsBadgeItem={dataEstructuraOrganica}
            />
          }
        />
        <ModalIMG
          idModal="Atribuciones"
          linkIMG="https://itetlax.org.mx/assets/img/OIC/1.jpg"
          altIMG="Atribuciones"
        />
        <Modal
          idModal="Normatividad"
          titleModal="Normatividad"
          sizeModal=""
          contentModal={
            <ListBadge ifNumbered={""} listsBadgeItem={dataNormatividad} />
          }
        />
        <Modal
          idModal="Programas"
          titleModal="Programas"
          sizeModal=""
          contentModal={<ListBadge ifNumbered={""} listsBadgeItem={dataPOA} />}
        />
        <Modal
          idModal="Semblanza"
          titleModal="Semblanza"
          sizeModal=""
          contentModal={
            <ListBadge ifNumbered={""} listsBadgeItem={dataSemblanza} />
          }
        />
        <Modal
          idModal="DeclaracionPatrimonial"
          titleModal="Declaración Patrimonial"
          sizeModal=""
          contentModal={
            <ListBadge
              ifNumbered={""}
              listsBadgeItem={dataDeclaracionPatrimonial}
            />
          }
        />
        <Modal
          idModal="CodigodeEtica"
          titleModal="Codigo de Ética"
          sizeModal=""
          contentModal={
            <ListBadge ifNumbered={""} listsBadgeItem={dataCodigoEtica} />
          }
        />
        <Modal
          idModal="Informes"
          titleModal="Informes"
          sizeModal=""
          contentModal={
            <ListBadge ifNumbered={""} listsBadgeItem={dataInformes} />
          }
        />
        <Modal
          idModal="Convenios"
          titleModal="Convenios"
          sizeModal=""
          contentModal={
            <ListBadge ifNumbered={""} listsBadgeItem={dataConvenios} />
          }
        />
        <Modal
          idModal="Acuerdos"
          titleModal="Acuerdos"
          sizeModal=""
          contentModal={
            <ListBadge ifNumbered={""} listsBadgeItem={dataAcuerdosOIC} />
          }
        />
        <Modal
          idModal="Resoluciones"
          titleModal="Resoluciones"
          sizeModal=""
          contentModal={
            <ListBadge ifNumbered={""} listsBadgeItem={dataResoluciones} />
          }
        />
      </div>
    </>
  );
};
export default Oic;
